import * as React from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const HOURS: string[] = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
const MINUTES: string[] = ["00", "15", "30", "45"];

function splitHHmm(value: string): { h: string; m: string } {
  const [h = "00", m = "00"] = value.split(":");
  return { h, m };
}

function joinHHmm(h: string, m: string): string {
  return `${h}:${m}`;
}

function timeToMinutes(t: string): number {
  const { h, m } = splitHHmm(t);
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

function isInBlockedRange(
  time: string,
  blockedRanges: Array<{ start: string; end: string }>,
): boolean {
  const mins = timeToMinutes(time);
  return blockedRanges.some(({ start, end }) => {
    const s = timeToMinutes(start);
    const e = timeToMinutes(end);
    return mins >= s && mins < e;
  });
}

export interface TimeSlotPickerValue {
  start: string;
  end: string;
  crossMidnight: boolean;
}

export interface TimeSlotPickerProps {
  value: TimeSlotPickerValue;
  onChange: (v: TimeSlotPickerValue) => void;
  blockedRanges?: Array<{ start: string; end: string }>;
  dstStatusFor: (h: number, m: number) => "valid" | "nonexistent" | "ambiguous";
}

interface HourMinuteSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  isDisabledTime: (time: string) => boolean;
}

function HourMinuteSelect({ id, label, value, onChange, isDisabledTime }: HourMinuteSelectProps) {
  const { h, m } = splitHHmm(value);

  const hourEnabled = HOURS.filter((hh) => MINUTES.some((mm) => !isDisabledTime(joinHHmm(hh, mm))));
  const minuteEnabled = MINUTES.filter((mm) => !isDisabledTime(joinHHmm(h, mm)));

  const selectClass =
    "h-9 rounded-4 border border-line bg-surface px-2 font-mono text-sm tabular-nums text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-p-600";

  function emitHour(nextH: string) {
    if (isDisabledTime(joinHHmm(nextH, m))) {
      const fallback = MINUTES.find((mm) => !isDisabledTime(joinHHmm(nextH, mm)));
      if (!fallback) return;
      onChange(joinHHmm(nextH, fallback));
      return;
    }
    onChange(joinHHmm(nextH, m));
  }

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={`${id}-hour`}>{label}</Label>
      <div className="flex items-center gap-1">
        <select
          id={`${id}-hour`}
          value={h}
          onChange={(e) => emitHour(e.target.value)}
          className={cn(selectClass, "w-16")}
          aria-label={`${label} – uren`}
        >
          {HOURS.map((hh) => (
            <option key={hh} value={hh} disabled={!hourEnabled.includes(hh)}>
              {hh}
            </option>
          ))}
        </select>
        <span className="font-mono text-ink-500">:</span>
        <select
          id={`${id}-minute`}
          value={m}
          onChange={(e) => onChange(joinHHmm(h, e.target.value))}
          className={cn(selectClass, "w-16")}
          aria-label={`${label} – minuten`}
        >
          {MINUTES.map((mm) => (
            <option key={mm} value={mm} disabled={!minuteEnabled.includes(mm)}>
              {mm}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function TimeSlotPicker({
  value,
  onChange,
  blockedRanges = [],
  dstStatusFor,
}: TimeSlotPickerProps) {
  const { t } = useTranslation();

  const startMins = timeToMinutes(value.start);

  function isDisabledStart(time: string): boolean {
    const { h, m } = splitHHmm(time);
    if (dstStatusFor(parseInt(h, 10), parseInt(m, 10)) === "nonexistent") return true;
    if (isInBlockedRange(time, blockedRanges)) return true;
    return false;
  }

  function isDisabledEnd(time: string): boolean {
    const { h, m } = splitHHmm(time);
    if (dstStatusFor(parseInt(h, 10), parseInt(m, 10)) === "nonexistent") return true;
    if (isInBlockedRange(time, blockedRanges)) return true;
    if (!value.crossMidnight && timeToMinutes(time) <= startMins) return true;
    return false;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-3">
        <HourMinuteSelect
          id="time-slot-start"
          label={t("time-slot.start.label")}
          value={value.start}
          onChange={(start) => onChange({ ...value, start })}
          isDisabledTime={isDisabledStart}
        />
        <HourMinuteSelect
          id="time-slot-end"
          label={t("time-slot.end.label")}
          value={value.end}
          onChange={(end) => onChange({ ...value, end })}
          isDisabledTime={isDisabledEnd}
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="cross-midnight"
          checked={value.crossMidnight}
          onCheckedChange={(checked) => onChange({ ...value, crossMidnight: checked })}
        />
        <Label htmlFor="cross-midnight" className="cursor-pointer">
          {t("time-slot.crossMidnight.label")}
        </Label>
      </div>
    </div>
  );
}
